/**
 * The documents rail (docs/19 A3, part 6) — done, next, waiting, and skipped,
 * **always with the reason** (stop list 7, rule 3).
 *
 * Every document of the pinned process version is on it, named by the
 * definition's own words — never a number an officer is asked to learn. What a
 * row may offer (Draft, View, Record it) is the server's answer, read off the
 * row: `generatable` is `_generatability`'s, reachability is the definition's,
 * and the *next* row is the card's own document. Nothing here works any of it
 * out for itself.
 *
 * Four groups an officer reads, and one — what is still ahead — that is closed
 * until opened: a stage the case has not reached is shape, not work. Closed is
 * not hidden; the count is on the header and one click opens it.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { api } from '../../api/client'
import type { CaseDetail, DocumentRow } from '../../api/client'
import { CitationChip, ErrorEnvelope } from '../../components/bits'
import { isAnsweredParawise } from '../../components/pleading'
import { isCourtDocument } from '../../lib/court'
import { readDate } from '../../lib/period'
import { useRefreshCase } from './acting'

/** Waiting on a fact an officer can put on the record — not off the path. */
export const waitingOnARecord = (row: DocumentRow): boolean =>
  row.state === 'skipped' && !row.recorded_state && (row.unlocking_facts ?? []).length > 0

type Group = 'next' | 'waiting' | 'done' | 'ahead' | 'skipped'

/**
 * Which group a row is in — exactly one, asked in the order the officer would ask.
 *
 * *Waiting* is a paper somebody else writes and the case expects — the enquiry
 * report, the inquiry report, the penalty order — while it is on the case's
 * path. A document that turns on a decision nobody has taken yet is not
 * waited for; it is *still ahead*, with the sentence that says what would bring
 * it in, and so is a document waiting on a fact that has not happened (an
 * appeal, an O.A.), with its *Record it* beside it.
 */
function groupOf(row: DocumentRow, nextCode: string | null | undefined): Group {
  if (row.document_type === nextCode) return 'next'
  if (waitingOnARecord(row)) return 'ahead'
  if (row.state === 'skipped' || row.recorded_state || row.state === 'out-of-scope')
    return 'skipped'
  if (row.occurrences > 0) return 'done'
  if (row.state === 'awaited' && !row.contingent) return 'waiting'
  return 'ahead'
}

const GROUPS: { key: Group; title: string; open: boolean }[] = [
  { key: 'next', title: 'Next', open: true },
  { key: 'waiting', title: 'Waiting', open: true },
  { key: 'done', title: 'Done', open: true },
  { key: 'ahead', title: 'Still ahead', open: false },
  { key: 'skipped', title: 'Skipped — each with its reason', open: false },
]

/**
 * The stage a court case's own documents belong to, or null for any other case.
 *
 * A case entered at the Tribunal and High Court stage — the officer said so
 * opening it, or its one card is a court document — listed sixteen documents of
 * the earlier stages as *Still ahead*, each with a Draft button, and the enquiry
 * report and the penalty order as *Waiting*, over a file that holds an Original
 * Application (walked on screen, 5 Oct 2026). For such a case the groups below
 * hold the court stage's documents, and every other document goes into one
 * group of its own, closed until opened. **Nothing is taken off the list**
 * (stop list 7): each row is there with its own state and its own reason.
 */
function courtStageOf(data: CaseDetail): string | null {
  const code = [data.next_action?.document, data.declared_reached].find((c) =>
    isCourtDocument(c),
  )
  if (!code) return null
  return (data.rail ?? []).find((row) => row.document_type === code)?.stage ?? null
}

export function DocumentsRail({ data, readOnly }: { data: CaseDetail; readOnly: boolean }) {
  const nextCode = data.next_action?.document ?? null
  const courtStage = courtStageOf(data)
  const byGroup = new Map<Group, DocumentRow[]>()
  const earlier: DocumentRow[] = []
  for (const row of data.rail ?? []) {
    if (courtStage && row.stage !== courtStage) {
      earlier.push(row)
      continue
    }
    const group = groupOf(row, nextCode)
    byGroup.set(group, [...(byGroup.get(group) ?? []), row])
  }
  return (
    <section className="rail" data-testid="documents-rail">
      <h3>The documents</h3>
      {GROUPS.map((group) => (
        <RailGroup
          key={group.key}
          id={group.key}
          title={group.title}
          startOpen={group.open}
          rows={byGroup.get(group.key) ?? []}
          data={data}
          readOnly={readOnly}
        />
      ))}
      {courtStage ? (
        <EarlierStages rows={earlier} nextCode={nextCode} data={data} readOnly={readOnly} />
      ) : null}
      {!readOnly ? <StageByHand data={data} /> : null}
    </section>
  )
}

/** The documents of the stages before the court stage, on a court case: one
 *  group, closed until opened, each row exactly as it would otherwise read. */
function EarlierStages({
  rows,
  nextCode,
  data,
  readOnly,
}: {
  rows: DocumentRow[]
  nextCode: string | null
  data: CaseDetail
  readOnly: boolean
}) {
  const [open, setOpen] = useState(false)
  if (rows.length === 0) return null
  return (
    <div className="rail-group ahead" data-testid="rail-earlier">
      <button
        type="button"
        className="rg-head"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
      >
        <span className="caret" aria-hidden="true">
          {open ? '▾' : '▸'}
        </span>
        <span className="ti">Documents of the earlier stages</span>
        <span className="cnt mono">{rows.length}</span>
      </button>
      {open ? (
        <>
          <p className="small muted rg-empty">
            This file is here for its court documents. The documents of the earlier stages are
            listed for the record, each as the case stands on it; none is asked for above.
          </p>
          <div className="rg-rows">
            {rows.map((row) => (
              <RailRow
                key={row.document_type}
                row={row}
                data={data}
                readOnly={readOnly}
                group={groupOf(row, nextCode)}
              />
            ))}
          </div>
        </>
      ) : null}
    </div>
  )
}

function RailGroup({
  id,
  title,
  startOpen,
  rows,
  data,
  readOnly,
}: {
  id: Group
  title: string
  startOpen: boolean
  rows: DocumentRow[]
  data: CaseDetail
  readOnly: boolean
}) {
  const [open, setOpen] = useState(startOpen)
  return (
    <div className={`rail-group ${id}`} data-testid={`rail-${id}`}>
      <button
        type="button"
        className="rg-head"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
      >
        <span className="caret" aria-hidden="true">
          {open ? '▾' : '▸'}
        </span>
        <span className="ti">{title}</span>
        <span className="cnt mono">{rows.length}</span>
      </button>
      {open ? (
        rows.length === 0 ? (
          <p className="small muted rg-empty">None.</p>
        ) : (
          <div className="rg-rows">
            {rows.map((row) => (
              <RailRow
                key={row.document_type}
                row={row}
                data={data}
                readOnly={readOnly}
                group={id}
              />
            ))}
          </div>
        )
      ) : null}
    </div>
  )
}

function RailRow({
  row,
  data,
  readOnly,
  group,
}: {
  row: DocumentRow
  data: CaseDetail
  readOnly: boolean
  group: Group
}) {
  const refresh = useRefreshCase(data.id)
  return (
    <div
      className={`rail-row ${group}`}
      data-testid={`doc-${row.document_type}`}
      data-state={row.state}
    >
      <span className="rr-name">
        {row.short_title ?? row.title}
        {row.open_repeatable ? (
          <span className="small muted"> · {row.occurrences} so far, still open</span>
        ) : null}{' '}
        <CitationChip citation={row.citation} ruleRefs={row.rule_refs} />
      </span>
      <span className="rr-why small">
        {row.recorded_state ? (
          <>
            {row.recorded_state === 'WITHDRAWN' ? 'Withdrawn' : 'Skipped'} on this case —{' '}
            {row.recorded_state_reason}
          </>
        ) : waitingOnARecord(row) ? (
          <>Not on the path until this is recorded: {row.condition_label ?? 'its condition'}</>
        ) : row.state === 'skipped' ? (
          (row.skip_reason ?? 'Not on this case’s path.')
        ) : row.state === 'out-of-scope' ? (
          'Does not apply to this case.'
        ) : row.contingent ? (
          <>
            Becomes due if: {row.becomes_reachable_if ?? 'its condition is met'}
            {row.awaiting_decision_label ? ` — awaiting: ${row.awaiting_decision_label}` : ''}
          </>
        ) : row.state === 'awaited' ? (
          `Awaited from ${row.author}.`
        ) : row.issued_on ? (
          `Issued ${readDate(row.issued_on)}${row.served_on ? `, served ${readDate(row.served_on)}` : ''}.`
        ) : null}
      </span>
      <span className="rr-act">
        {!readOnly && waitingOnARecord(row) ? (
          <RecordTheFact row={row} caseId={data.id} onDone={refresh} />
        ) : row.occurrences > 0 ? (
          <Link className="btn" to={`/cases/${data.id}/documents/${row.document_type}`}>
            View
          </Link>
        ) : !readOnly && row.generatable && group !== 'skipped' ? (
          <Link className="btn" to={`/cases/${data.id}/documents/${row.document_type}`}>
            Draft
          </Link>
        ) : !readOnly && isAnsweredParawise(row.document_type) && row.state !== 'skipped' ? (
          <Link className="btn" to={`/cases/${data.id}/documents/${row.document_type}`}>
            Draft the reply
          </Link>
        ) : null}
      </span>
    </div>
  )
}

/**
 * Put on the record the fact a document is waiting on — `record-fact`, which
 * accepts exactly the paths some rule in force reads. The date is the day the
 * thing happened, which on a pleading served by post is not the day he types —
 * so it starts blank, as it did on the page before this one, and never as
 * today (an appeal's filing date is what its period turns on).
 */
function RecordTheFact({
  row,
  caseId,
  onDone,
}: {
  row: DocumentRow
  caseId: string
  onDone: () => void
}) {
  const [open, setOpen] = useState(false)
  const [on, setOn] = useState('')
  const path = (row.unlocking_facts ?? [])[0]
  const record = useMutation({
    mutationFn: () =>
      api.recordFact(caseId, {
        preflight: false,
        path: path ?? '',
        value: on,
        recorded_on: on,
        note: null,
      }),
    onSuccess: () => {
      setOpen(false)
      onDone()
    },
  })
  if (!path) return null
  if (!open) {
    return (
      <button
        type="button"
        className="btn"
        onClick={() => setOpen(true)}
        data-testid={`record-${row.document_type}`}
      >
        Record it
      </button>
    )
  }
  return (
    <span className="record-fact">
      <input
        type="date"
        value={on}
        onChange={(e) => setOn(e.target.value)}
        aria-label={`The date ${row.condition_label ?? 'it happened'}`}
      />
      <button
        type="button"
        className="btn"
        disabled={!on || record.isPending}
        onClick={() => record.mutate()}
      >
        {record.isPending ? 'Recording…' : 'Record'}
      </button>
      <button type="button" className="quiet" onClick={() => setOpen(false)}>
        Cancel
      </button>
      <ErrorEnvelope error={record.error} />
    </span>
  )
}

/**
 * **The stage moves itself** as the acts it waits on are recorded — the same
 * guards decide. Since 5 Oct 2026 a stage a recorded decision has taken off
 * the case (the formal inquiry, where he admitted the charges or the authority
 * decided to hold none) is passed over rather than waited at, which is most of
 * the case this control was kept for (finding B-08).
 *
 * **Not all of it.** A Rule 16 case with no representation on which an inquiry
 * is ordered under 16(1)(b) still cannot enter the inquiry stage by itself:
 * its guard reads a reply period the Rule 16 memorandum never starts. For that
 * case, and for any on which the register cannot work the stage out, moving it
 * by hand is the only way forward — so it stays. It answers to the guards
 * alone, and a refusal is the server's envelope: the rule, the citation, what
 * is missing (walkthrough log 32.36).
 */
function StageByHand({ data }: { data: CaseDetail }) {
  const refresh = useRefreshCase(data.id)
  const enter = useMutation({
    mutationFn: (stage: string) =>
      api.enterStage(data.id, { preflight: false, stage, entered_on: data.as_of }),
    onSuccess: refresh,
  })
  const stages = (data.stages ?? []).filter(
    (stage) => stage.in_scope && stage.id !== data.stage,
  )
  if (stages.length === 0) return null
  return (
    <details className="stage-by-hand" data-testid="stage-by-hand">
      <summary>The stage moves itself as its acts are recorded — move it by hand</summary>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        {stages.map((stage) => (
          <button
            key={stage.id}
            type="button"
            className="quiet"
            disabled={enter.isPending}
            onClick={() => enter.mutate(stage.id)}
            data-testid={`enter-stage-${stage.n}`}
          >
            Move the case into stage {stage.n} · {stage.title}
          </button>
        ))}
      </div>
      <ErrorEnvelope error={enter.error} />
    </details>
  )
}
